import React, { useState } from 'react';
import { Send, RotateCcw, Shuffle } from 'lucide-react';
import { DEMO_FILE } from '@/lib/useClaimDemo';

const TEST_PRINCIPAL = '0x3f51beeA4C65FBEEac6246d5Cef656b77338BBD8';
const input = 'w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-amber-400/60 outline-none';
const btn = 'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40';

function Toggle({ id, label, hint, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 cursor-pointer">
      <input type="checkbox" className="mt-1 accent-amber-300" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={id} />
      <span><span className="block text-sm text-white">{label}</span><span className="block text-xs text-gray-500">{hint}</span></span>
    </label>
  );
}

function Field({ label, children }) {
  return <label className="block space-y-1"><span className="text-xs text-gray-400">{label}</span>{children}</label>;
}

export function DemoClaimForm({ onSubmit, sent, prefill }) {
  const [expiry] = useState(() => Math.floor(Date.now() / 1000) + 86400);
  const [opts, setOpts] = useState({ loseReceipt: false, slowMerchant: false });
  const [f, setF] = useState({ principal: TEST_PRINCIPAL, agentId: 'agent.example/assistant', orderRef: 'ORD-20001', claimType: 'cancellation', claimNumber: 1, notice: 'Cancel before shipping' });
  React.useEffect(() => {
    if (prefill) setF((p) => ({ ...p, orderRef: prefill.orderRef, notice: prefill.notice }));
  }, [prefill]);
  const set = (k) => (e) => setF({ ...f, [k]: k === 'claimNumber' ? Number(e.target.value) : e.target.value });
  const delegation = (extra = {}) => ({ ...f, merchant: DEMO_FILE.merchant, expiry, ...extra });
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="demo-form">
      <h2 className="text-base md:text-lg font-semibold mb-1">1. Send a test claim</h2>
      <p className="text-sm text-gray-400 mb-4">A test Claim Delegation to the demo merchant shop.example. Test address only.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Principal (test address)"><input className={`${input} font-mono text-xs`} value={f.principal} onChange={set('principal')} data-testid="demo-principal-input" /></Field>
        <Field label="Agent"><input className={input} value={f.agentId} onChange={set('agentId')} data-testid="demo-agent-input" /></Field>
        <Field label="Order">
          <select className={input} value={f.orderRef} onChange={set('orderRef')} data-testid="demo-order-select">
            <option value="ORD-20001">ORD-20001 (known order)</option>
            <option value="ORD-20002">ORD-20002 (known order)</option>
            <option value="ORD-UNKNOWN">ORD-UNKNOWN (not in the order system)</option>
          </select>
        </Field>
        <Field label="Claim type">
          <select className={input} value={f.claimType} onChange={set('claimType')} data-testid="demo-claim-type-select">
            <option value="cancellation">cancellation (in pilot scope)</option>
            <option value="refund">refund (out of pilot scope)</option>
          </select>
        </Field>
        <Field label="Claim number"><input type="number" min="1" max="65535" className={input} value={f.claimNumber} onChange={set('claimNumber')} data-testid="demo-claim-number-input" /></Field>
        <Field label="Notice"><input className={input} value={f.notice} onChange={set('notice')} data-testid="demo-notice-input" /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="demo-delivery-toggles">
        <Toggle id="demo-lose-receipt-toggle" label="Lose the receipt on the way back" hint="The merchant gets the claim, but its reply never reaches the agent." checked={opts.loseReceipt} onChange={(v) => setOpts({ ...opts, loseReceipt: v })} />
        <Toggle id="demo-slow-merchant-toggle" label="Merchant is slow" hint="A receipt comes back, but no &quot;received&quot; event yet." checked={opts.slowMerchant} onChange={(v) => setOpts({ ...opts, slowMerchant: v })} />
      </div>
      <p className="text-xs text-gray-500 mt-2">Switches apply to a new claim. A retry always gets the original receipt back.</p>
      <div className="flex flex-wrap gap-3 mt-5">
        <button type="button" onClick={() => onSubmit(delegation(), opts)} className={`${btn} bg-amber-300 text-black hover:bg-amber-200`} data-testid="demo-send-btn"><Send className="w-4 h-4" /> Send claim</button>
        <button type="button" disabled={!sent} onClick={() => onSubmit(delegation())} className={`${btn} border border-white/15 text-white hover:border-white/40`} data-testid="demo-retry-btn"><RotateCcw className="w-4 h-4" /> Send again (retry)</button>
        <button type="button" disabled={!sent} onClick={() => onSubmit(delegation({ notice: `${f.notice} (changed)` }))} className={`${btn} border border-white/15 text-white hover:border-white/40`} data-testid="demo-reuse-btn"><Shuffle className="w-4 h-4" /> Reuse number, change content</button>
      </div>
    </section>
  );
}
